import { InputField } from '@components/InputField';
import Tooltip from '@components/Tooltip';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { preventDefaultEnterSubmit } from '@helpers/preventDefaultEnterSubmit';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';

const optionClasses = cva('flex cursor-pointer items-start border-x transition', {
  variants: {
    isChecked: {
      true: 'border-blue-200 bg-blue-100',
      false: 'border-info-200',
    },
    isFirst: { true: 'rounded-t-lg border-t', false: '' },
    isLast: { true: 'rounded-b-lg border-b', false: '' },
    isDisabled: { true: 'cursor-not-allowed opacity-50', false: '' },
  },
  compoundVariants: [{ isChecked: false, isDisabled: false, class: 'hover:bg-info-50' }],
  defaultVariants: { isDisabled: false },
});

const titleClasses = cva('text-sm leading-5 font-medium', {
  variants: {
    isChecked: {
      true: 'text-blue-900',
      false: 'text-info-900',
    },
  },
});

const descriptionClasses = cva('text-xs leading-4 font-normal', {
  variants: {
    isChecked: {
      true: 'text-blue-700',
      false: 'text-info-500',
    },
  },
});

export interface RadioCardOption<T extends string> {
  value: T;
  title: string;
  description: string;
  dataTestId?: string;
  isDisabled?: boolean;
  tooltipContent?: string;
}

interface RadioCardGroupProps<T extends string> {
  labelText: string;
  options: RadioCardOption<T>[];
  value: T;
  onChange: (value: T) => void;
  onBlur?: () => void;
}

export const RadioCardGroup = <T extends string>({
  labelText,
  options,
  value,
  onChange,
  onBlur,
}: RadioCardGroupProps<T>) => (
  <InputField labelText={labelText}>
    <RadioGroup
      value={value}
      onValueChange={(next) => onChange(next as T)}
      onBlur={onBlur}
      className="gap-0 rounded-lg shadow-xs"
      onKeyDown={preventDefaultEnterSubmit}
    >
      {options.map((option, index) => {
        const isChecked = option.value === value;
        const isPreviousChecked = index > 0 && options[index - 1]!.value === value;

        const card = (
          <label
            className={optionClasses({
              isChecked,
              isFirst: index === 0,
              isLast: index === options.length - 1,
              isDisabled: option.isDisabled,
            })}
            data-testid={option.dataTestId}
          >
            <RadioGroupItem
              value={option.value}
              disabled={option.isDisabled}
              className="mt-4 ml-4"
              id={option.dataTestId}
            />
            <div className="flex flex-1 flex-col gap-1 p-4 pl-3">
              <span className={titleClasses({ isChecked })}>{option.title}</span>
              <span className={descriptionClasses({ isChecked })}>{option.description}</span>
            </div>
          </label>
        );

        return (
          <div key={option.value} className="contents">
            {index > 0 && (
              <div className={clsx('h-px w-full', isChecked || isPreviousChecked ? 'bg-blue-200' : 'bg-info-200')} />
            )}
            {option.tooltipContent ? (
              <Tooltip disabled={!option.isDisabled} content={option.tooltipContent}>
                {card}
              </Tooltip>
            ) : (
              card
            )}
          </div>
        );
      })}
    </RadioGroup>
  </InputField>
);
